import { useEffect, useState } from "react";
import { Alert, Input, Modal, Typography, notification } from "antd";
import { useTranslation } from "react-i18next";
import { ApiService } from "../api";
import { apiErrorMessage } from "../api/apiError";

const { Text } = Typography;

type Props = {
  templateId?: number;
  templateName?: string;
  shortName?: string;
  visible: boolean;
  setVisible: (visible: boolean) => void;
  onDone: (created: { id: number; short_name: string }) => void;
};

/**
 * A full copy of a process, under a new short name.
 *
 * The blank canvas is the most expensive moment in this product. "The same as
 * Purchase Requisition but for services" currently means rebuilding nine
 * sections, six steps, four sign-offs and a lifecycle by hand.
 *
 * Two things this dialog is careful about:
 *
 * * **The short name is the identity.** It is in every case number this process
 *   will ever produce, so it cannot be changed casually afterwards, and it must
 *   not collide. The server refuses a duplicate with a sentence; this asks for
 *   it up front and defaults to something plausible.
 * * **Warnings are not errors.** A copy is often 95% complete — a group that
 *   exists on the original but not here, documents that were never exported.
 *   Those are reported *after* it is created, because a template that is
 *   nearly right and honest about the rest is far more use than a refusal.
 */
const CloneTemplateModal = ({
  templateId,
  templateName,
  shortName,
  visible,
  setVisible,
  onDone,
}: Props) => {
  const { t } = useTranslation();
  const [newShortName, setNewShortName] = useState("");
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!visible) return;
    // Plausible defaults, not empty boxes: most copies are "the same but…",
    // and an empty required field is one more thing to think about.
    setNewShortName(shortName ? `${shortName}-COPY` : "");
    setNewName(templateName ? `${templateName} (copy)` : "");
  }, [visible, shortName, templateName]);

  const submit = async () => {
    if (!templateId) return;
    setBusy(true);
    try {
      const created = await ApiService.apiMainTemplateCloneCreate(templateId, {
        short_name: newShortName.trim(),
        name: newName.trim(),
      });
      notification.success({
        message: t("'{{name}}' was copied.", { name: created.name }),
        description: created.warnings.length
          ? created.warnings.join(" ")
          : undefined,
        // Long enough to actually read the warnings, which are the whole point
        // of returning them.
        duration: created.warnings.length ? 12 : 4,
      });
      setVisible(false);
      onDone(created);
    } catch (e: any) {
      notification.error({
        message: apiErrorMessage(e, t("Something went wrong.")),
      });
    }
    setBusy(false);
  };

  return (
    <Modal
      title={t("Copy this process")}
      open={visible}
      onCancel={() => setVisible(false)}
      onOk={submit}
      okText={t("Make a copy")}
      okButtonProps={{ disabled: !newShortName.trim(), loading: busy }}
      cancelText={t("Cancel")}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message={t(
          "Everything is copied: sections and their questions, steps, sign-offs, signature slots, rules and the lifecycle. Cases are not — the copy starts empty."
        )}
      />
      <div style={{ marginBottom: 10 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t("Short name")}
        </Text>
        <Input
          value={newShortName}
          placeholder="PR-SERVICES"
          onChange={(e) => setNewShortName(e.target.value)}
        />
        <Text type="secondary" style={{ fontSize: 11 }}>
          {t(
            "It appears in every case number this process produces, so pick it carefully."
          )}
        </Text>
      </div>
      <div>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t("Name")}
        </Text>
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
      </div>
    </Modal>
  );
};

export default CloneTemplateModal;
